import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const ActivityLog = ({ activities, isExpanded, onToggleExpanded }) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef(null);

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [activities, autoScroll]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'success':
        return 'CheckCircle';
      case 'failed':
        return 'XCircle';
      case 'retry':
        return 'RotateCcw';
      case 'duplicate':
        return 'Copy';
      case 'searching':
        return 'Search';
      case 'info':
        return 'Info';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-success';
      case 'failed':
        return 'text-error';
      case 'retry':
        return 'text-warning';
      case 'duplicate':
        return 'text-accent';
      case 'searching':
        return 'text-primary';
      case 'info':
        return 'text-text-secondary';
      default:
        return 'text-text-muted';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleScroll = () => {
    if (logRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setAutoScroll(isAtBottom);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-text-primary" />
          <h3 className="text-lg font-medium text-text-primary">Activity Log</h3>
          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
            {activities.length} events
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`p-2 rounded-lg transition-smooth ${
              autoScroll 
                ? 'bg-primary/10 text-primary' :'bg-surface-secondary text-text-secondary hover:text-text-primary'
            }`}
            title={autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
          >
            <Icon name="ArrowDown" size={16} />
          </button>
          
          <button
            onClick={onToggleExpanded}
            className="lg:hidden p-2 rounded-lg bg-surface-secondary text-text-secondary hover:text-text-primary transition-smooth"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>
        </div>
      </div>

      <div 
        ref={logRef}
        className={`overflow-y-auto transition-all duration-300 ${
          isExpanded ? 'max-h-96' : 'max-h-48 lg:max-h-64'
        }`}
        onScroll={handleScroll}
      >
        <div className="p-4 space-y-3">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <Icon name="Clock" size={24} className="mx-auto mb-2 opacity-50" />
              <p>Waiting for sync to begin...</p>
            </div>
          ) : (
            activities.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-background rounded-lg">
                <div className={`flex-shrink-0 mt-0.5 ${getActivityColor(activity.type)}`}>
                  <Icon name={getActivityIcon(activity.type)} size={16} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-text-primary leading-relaxed">
                        {activity.message}
                      </p>
                      {activity.details && (
                        <p className="text-xs text-text-muted mt-1">
                          {activity.details}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-text-muted ml-2 flex-shrink-0">
                      {formatTime(activity.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;